import { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Loader2, CheckCircle, AlertCircle, Eye, EyeOff, ArrowRight } from 'lucide-react'

interface CheckoutSessionData {
  email: string
  name: string
  customer_id: string
  subscription_id: string
  plan: string
  status: string
  payment_status: string
}

const API_BASE_URL = (import.meta.env.VITE_API_URL || 'https://app.pongofin.com/api').replace(/\/$/, '')

export default function Register() {
  const [searchParams] = useSearchParams()
  
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [sessionData, setSessionData] = useState<CheckoutSessionData | null>(null)
  
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [pecAddress, setPecAddress] = useState('')
  const [pecUsername, setPecUsername] = useState('')
  const [pecPassword, setPecPassword] = useState('')
  const [showPecPassword, setShowPecPassword] = useState(false)
  const [registering, setRegistering] = useState(false)
  const [success, setSuccess] = useState(false)

  const checkoutSession = searchParams.get('checkout_session')
  const plan = searchParams.get('plan')

  const fetchCheckoutSession = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/stripe/checkout-session?session_id=${checkoutSession}`)
      
      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Errore nel recupero dei dati')
      }

      const data = await response.json()
      setSessionData(data)
    } catch (err) {
      console.error('Fetch checkout session error:', err)
      setError(err instanceof Error ? err.message : 'Errore nel recupero dei dati')
    } finally {
      setLoading(false)
    }
  }, [checkoutSession])

  useEffect(() => {
    if (!checkoutSession) {
      setError('Sessione di checkout non valida. Torna alla pagina dei prezzi.')
      setLoading(false)
      return
    }

    fetchCheckoutSession()
  }, [checkoutSession, fetchCheckoutSession])

  useEffect(() => {
    if (!pecUsername && sessionData?.email) {
      setPecUsername(sessionData.email)
    }
  }, [pecUsername, sessionData?.email])

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (password.length < 8) {
      setError('La password deve essere di almeno 8 caratteri')
      return
    }
    
    if (password !== confirmPassword) {
      setError('Le password non coincidono')
      return
    }

    if (!sessionData?.email) {
      setError('Email non trovata')
      return
    }

    if (!pecAddress.includes('@')) {
      setError('Inserisci un indirizzo PEC valido')
      return
    }

    if (!pecUsername.trim()) {
      setError('Inserisci lo username PEC')
      return
    }

    if (!pecPassword.trim()) {
      setError('Inserisci la password PEC')
      return
    }

    setRegistering(true)
    setError(null)

    try {
      // Register the user with Stripe data
      const response = await fetch(`${API_BASE_URL}/auth/register-from-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: sessionData.email,
          password: password,
          name: sessionData.name || '',
          stripe_customer_id: sessionData.customer_id,
          stripe_subscription_id: sessionData.subscription_id,
          plan: sessionData.plan || plan || 'starter',
          checkout_session: checkoutSession,
          pec_address: pecAddress.trim().toLowerCase(),
          pec_username: pecUsername.trim(),
          pec_password: pecPassword,
          pec_provider: pecAddress.split('@')[1] || '',
        })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Errore durante la registrazione')
      }

      // Save token and redirect
      if (data.token) {
        localStorage.setItem('access_token', data.token)
        setSuccess(true)
        
        // Redirect to dashboard after 2 seconds
        setTimeout(() => {
          window.location.href = '/'
        }, 2000)
      } else {
        throw new Error('Token non ricevuto')
      }
    } catch (err) {
      console.error('Registration error:', err)
      setError(err instanceof Error ? err.message : 'Errore durante la registrazione')
    } finally {
      setRegistering(false)
    }
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-[20px] shadow-modal p-8 max-w-md w-full text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-800">Verifica pagamento...</h2>
          <p className="text-slate-500 mt-2">Stiamo verificando i dettagli del tuo abbonamento</p>
        </div>
      </div>
    )
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-[20px] shadow-modal p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Account creato!</h2>
          <p className="text-slate-500 mb-6">
            Benvenuto in PongoFin. Stai per essere reindirizzato...
          </p>
          <div className="animate-pulse text-primary font-medium">
            Reindirizzamento in corso...
          </div>
        </div>
      </div>
    )
  }

  // Error state without session data
  if (error && !sessionData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-[20px] shadow-modal p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Errore</h2>
          <p className="text-slate-500 mb-6">{error}</p>
          <a 
            href="https://pongofin.com#pricing"
            className="inline-flex items-center gap-2 bg-primary text-white h-[44px] px-6 rounded-[12px] font-semibold hover:bg-primary-dark hover:-translate-y-px hover:shadow-btn-hover active:scale-[0.98] transition-all duration-200 ease-apple"
          >
            Torna ai prezzi
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    )
  }

  // Registration form
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-[20px] shadow-modal p-8 max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <div 
            className="w-16 h-16 mx-auto mb-4 rounded-full"
            style={{
              background: 'radial-gradient(circle at 30% 30%, #00c853 0%, #00e676 25%, #69f0ae 50%, #b9f6ca 75%, #e8f5e9 100%)',
              boxShadow: '0 4px 20px rgba(0, 200, 83, 0.3)'
            }}
          />
          <h1 className="text-2xl font-bold text-slate-800">Completa la registrazione</h1>
          <p className="text-slate-500 mt-2">
            Il pagamento è andato a buon fine. Inserisci credenziali PEC e password account per attivare il flusso automatico.
          </p>
        </div>

        {/* Plan badge */}
        {sessionData?.plan && (
          <div className="flex justify-center mb-6">
            <span className="bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-semibold">
              Piano {sessionData.plan.charAt(0).toUpperCase() + sessionData.plan.slice(1)} - 14 giorni gratis
            </span>
          </div>
        )}

        {/* Email display */}
        <div className="bg-slate-50 rounded-xl p-4 mb-6">
          <p className="text-sm text-slate-500 mb-1">Email registrata</p>
          <p className="font-semibold text-slate-800">{sessionData?.email}</p>
        </div>

        {/* Error message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Registration form */}
        <form onSubmit={handleRegister} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              PEC Accreditata
            </label>
            <input
              type="email"
              value={pecAddress}
              onChange={(e) => setPecAddress(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
              placeholder="nome@pec.it"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Username PEC
            </label>
            <input
              type="text"
              value={pecUsername}
              onChange={(e) => setPecUsername(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
              placeholder="username PEC"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Password PEC
            </label>
            <div className="relative">
              <input
                type={showPecPassword ? 'text' : 'password'}
                value={pecPassword}
                onChange={(e) => setPecPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-primary focus:border-transparent transition-all pr-12"
                placeholder="Password PEC"
                required
              />
              <button
                type="button"
                onClick={() => setShowPecPassword(!showPecPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPecPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-primary focus:border-transparent transition-all pr-12"
                placeholder="Minimo 8 caratteri"
                required
                minLength={8}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Conferma Password
            </label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
              placeholder="Ripeti la password"
              required
              minLength={8}
            />
          </div>

          <button
            type="submit"
            disabled={registering}
            className="w-full bg-primary text-white py-3 px-6 rounded-xl font-semibold hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {registering ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Creazione account...
              </>
            ) : (
              <>
                Crea Account
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>
        </form>

        {/* Footer */}
        <p className="text-center text-sm text-slate-500 mt-6">
          Hai già un account?{' '}
          <a href="/" className="text-primary hover:underline font-medium">
            Accedi
          </a>
        </p>
      </div>
    </div>
  )
}
